// Files: a task's own (attach, open, take off), and every file shared in a project - on its tasks, in their comments,
// and in its chat - each for whoever could open it where it was shared.
import { useRef, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { fileUrl, formatShort, formatSize, uploadFile } from "../../lib/chat";
import { projectKeys, type FileRef, type Project, type ProjectFile } from "../../lib/projects";
import { Alert, Button, Card, Spinner, cx } from "../../components/ui";
import { Icon } from "../../components/icons";
import { errorText, useOrg } from "../org";

const isImage = (f: FileRef) => f.contentType.startsWith("image/");

// One file, as a row: a thumbnail for pictures, else an icon; the name opens it.
export function FileLine({ file, onRemove }: { file: FileRef; onRemove?: () => void }) {
  const { org } = useOrg();
  return (
    <li className="flex items-center gap-3">
      <a href={fileUrl(org.slug, file.id)} target="_blank" rel="noreferrer" className="flex min-w-0 flex-1 items-center gap-3 rounded-lg p-1 hover:bg-slate-50 dark:hover:bg-slate-800/60">
        {isImage(file)
          ? <img src={fileUrl(org.slug, file.id)} alt="" className="h-10 w-10 shrink-0 rounded-md object-cover ring-1 ring-slate-200 dark:ring-slate-700" />
          : <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-slate-100 text-slate-500 dark:bg-slate-800"><Icon name="file" className="h-5 w-5" /></span>}
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium">{file.filename}</span>
          <span className="block text-xs text-slate-500">{formatSize(file.size)}{file.uploader ? ` · ${file.uploader.name}` : ""} · {formatShort(file.createdAt)}</span>
        </span>
      </a>
      {onRemove && <button type="button" onClick={onRemove} aria-label={`Take ${file.filename} off`} className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600 dark:hover:bg-slate-800"><Icon name="x" className="h-4 w-4" /></button>}
    </li>
  );
}

// Choosing files and uploading them; gives back their ids when all are up.
export function useUploads() {
  const { org } = useOrg();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function upload(list: FileList | File[]): Promise<FileRef[]> {
    setBusy(true); setError(null);
    try {
      const done: FileRef[] = [];
      for (const f of [...list].slice(0, 10)) {
        const a = await uploadFile(org.slug, f, () => undefined);
        done.push({ ...a, uploader: null, createdAt: new Date().toISOString() });
      }
      return done;
    } catch (err) { setError(errorText(err)); return []; } finally { setBusy(false); }
  }
  return { upload, busy, error, setError };
}

export function AttachButton({ onFiles, busy, label = "Attach" }: { onFiles: (files: FileList) => void; busy: boolean; label?: string }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input ref={input} type="file" multiple hidden onChange={(e) => { if (e.target.files?.length) onFiles(e.target.files); e.target.value = ""; }} />
      <Button size="sm" variant="ghost" type="button" loading={busy} onClick={() => input.current?.click()}><Icon name="paperclip" className="h-4 w-4" />{label}</Button>
    </>
  );
}

export function ProjectFiles({ project, onOpen }: { project: Project; onOpen: (taskId: string) => void }) {
  const { org } = useOrg();
  const list = useQuery({ queryKey: projectKeys.files(org.slug, project.id), queryFn: () => api<{ files: ProjectFile[] }>(`/orgs/${org.slug}/projects/${project.id}/files`).then((r) => r.files) });
  if (list.isLoading) return <div className="flex justify-center p-10 text-brand-600"><Spinner /></div>;
  if (list.error) return <Alert>{errorText(list.error)}</Alert>;
  const files = list.data ?? [];
  if (!files.length) {
    return (
      <Card className="p-10 text-center">
        <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-brand-50 text-brand-600 dark:bg-brand-700/30"><Icon name="paperclip" /></div>
        <p className="font-medium">No files yet</p>
        <p className="mt-1 text-sm text-slate-500">Files attached to tasks, to comments, or shared in the project's chat all show up here.</p>
      </Card>
    );
  }
  return (
    <Card className="p-0">
      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {files.map((f) => (
          <li key={f.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2">
            <ul className="min-w-0 flex-1"><FileLine file={f} /></ul>
            <span className={cx("w-full pl-14 text-xs text-slate-500 sm:w-auto sm:pl-0")}>
              {f.from.kind === "chat"
                ? <Link to={`/o/${org.slug}/chat/${f.from.conversationId}`} className="hover:underline">in the {f.from.audience === "client" ? "chat with the client" : "project chat"}</Link>
                : <button type="button" onClick={() => onOpen((f.from as { taskId: string }).taskId)} className="hover:underline">{f.from.kind === "comment" ? "in a comment on " : "on "}{f.from.ref} {f.from.title}</button>}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
